import {
	createRouter,
	createWebHistory,
	RouteLocationNormalized,
	RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '@/composable/StoreUtils/useAuthStore'
import { useDesignSettingsStore } from '@/composable/StoreUtils/useDesignSettingsStore'
import api from '@/config/axios'
import MainLayout from '@/layouts/MainLayout.vue'
import QuickCanvasRoutes from '@/router/Modules/QuickCanvasRoutes'
import { settings } from '@/settings/DynamicSettings'
import { SingleSetupStore } from '@/store/modules/singleSetup'
import { rootStore, useSelector } from '@/store/rootStore'

type RouteWithMeta = RouteRecordRaw & {
	meta?: {
		requiresAuth?: boolean
		checkForCompanyRestrictions?: boolean
		requiresCompanyInfo?: boolean
	}
}

let hasLoadedSingleSetupData = false

const routes: Array<RouteWithMeta> = [
	{
		path: '/',
		component: MainLayout,
		redirect: { name: 'editor' },
		children: [...QuickCanvasRoutes],
	},
	{
		name: 'auth-error',
		path: '/auth/error',
		component: () =>
			import(/* webpackChunkName: "auth-error" */ '@/views/AuthError.vue'),
	},
	{
		name: 'auth-verify',
		path: '/auth/verify',
		component: () =>
			import(/* webpackChunkName: "auth-verify" */ '@/views/AuthVerify.vue'),
	},
	{
		name: '404',
		path: '/404',
		component: () => import(/* webpackChunkName: "404" */ '@/views/404.vue'),
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: () => import(/* webpackChunkName: "404" */ '@/views/404.vue'),
	},
]

const router = createRouter({
	history: createWebHistory(),
	routes,
})

function setIsTemplateFromSettings(value: boolean) {
	const { settings, setDesignSettings } = useDesignSettingsStore()
	setDesignSettings({ ...settings.value, isTemplate: value })
}

const navigationGuards = {
	requiresAuth: async (to: RouteLocationNormalized) => {
		if (!to.matched.some((record) => record.meta.requiresAuth)) return

		const {
			isAuthenticated,
			isAuthorized,
			tokenExchange,
			setResetPassword,
			removeResetPassword,
		} = useAuthStore()
		const { path, query, params } = to

		const sessionId = query.sessionId?.toString()
		const supplierId = query.supplier_id?.toString()
		const distributorId = query.distributor_id?.toString()
		const temporalToken = query.t?.toString()
		const type = query.type?.toString()
		const auth = query.auth?.toString()

		const isNewAuthWorkflow = useSelector(
			(state) => state.config.isNewAuthWorkflow
		)

		if (!isAuthenticated.value) {
			if (sessionId && supplierId) {
				await tokenExchange(sessionId, supplierId, distributorId)
			} else if (
				!auth ||
				!isNewAuthWorkflow.value ||
				(auth !== 'reset-password' && temporalToken)
			) {
				// if temporalToken is undefined, will use the storaged refreshToken value, if undefined as well, will not authenticate
				await isAuthorized(temporalToken)
			}
		}

		if (!isNewAuthWorkflow.value && !isAuthenticated.value) {
			window.location.href = import.meta.env.VITE_LOGIN_URL as string
		}

		const SINGLE_SETUP_API = new URL(
			settings.SINGLE_SETUP_URL(params.id?.toString())
		)
		if (distributorId) {
			SINGLE_SETUP_API.searchParams.set('distributor_id', distributorId)
		}

		const setEmptySingleSetupData = () => {
			rootStore.commit({
				type: 'singleSetup/setSingleSetupData',
				payload: {
					company_info: {},
					currencies: [],
					profiling: {},
					project: {},
					project_pages: {},
					settings: {},
					user: {},
				},
			})
		}

		// do not load if not logged in
		if (!hasLoadedSingleSetupData) {
			if (isAuthenticated.value) {
				try {
					const singleSetupResponse = await api
						.get<SingleSetupStore['data']>(SINGLE_SETUP_API.toString())
						.catch((error) => {
							switch (error.response?.status) {
								case 423:
									break
								case 403:
									error.customMessage =
										'Forbidden project not associated with the company'
									error.customMessageDelay = 0
									error.customMessageClosable = false
									throw error
								default:
									error.customMessage = 'Unable to load the project data'
									error.customMessageDelay = 0
									error.customMessageClosable = false
									throw error
							}
						})
					rootStore.commit({
						type: 'singleSetup/setSingleSetupData',
						payload: singleSetupResponse?.data,
					})
					hasLoadedSingleSetupData = true
				} catch (error) {
					setEmptySingleSetupData()
				}
			} else {
				setEmptySingleSetupData()
			}
		}

		const deleteQueryKeys = []

		if (!path && type === 'template' && isAuthenticated.value) {
			setIsTemplateFromSettings(true)
			deleteQueryKeys.push('type')
		}

		if (auth) {
			if (auth === 'reset-password' && temporalToken) {
				setResetPassword(temporalToken)
				deleteQueryKeys.push('auth', 't')
			} else {
				removeResetPassword()
			}
		} else if (temporalToken) {
			deleteQueryKeys.push('t')
		} else if (sessionId) {
			deleteQueryKeys.push(
				'sessionId',
				'supplier_id',
				'client_id',
				'distributor_id'
			)
		}

		if (deleteQueryKeys.length) {
			const newQuery = query
			deleteQueryKeys.forEach((key) => {
				delete newQuery[key]
			})
			return { query: newQuery, params }
		}
	},
	checkForCompanyRestrictions: async (to: RouteLocationNormalized) => {
		if (!to.matched.some((record) => record.meta.checkForCompanyRestrictions))
			return

		const singleSetupData = useSelector((state) => state.singleSetup.data)
		const companySettingsResponse = singleSetupData.value?.settings

		rootStore.commit({
			type: 'project/setCompanySettings',
			payload: companySettingsResponse,
		})

		if (!companySettingsResponse?.settings?.restrictions) return

		const restrictionKeyList: string[] = [
			'smart-layouts.edit',
			'smart-layouts.create',
			'smart-layouts-templates.edit',
			'smart-layouts-templates.create',
		]

		const hasRestriction = restrictionKeyList.some(
			(restrictionKey) =>
				restrictionKey in (companySettingsResponse?.settings?.restrictions ?? {})
		)
		if (hasRestriction) return '/404'
	},
	requiresCompanyInfo: async (to: RouteLocationNormalized) => {
		if (!to.matched.some((record) => record.meta.requiresCompanyInfo)) return

		const singleSetupData = useSelector((state) => state.singleSetup.data)
		const companyResponse = singleSetupData.value?.company_info
		const userResponse = singleSetupData.value?.user

		rootStore.commit({
			type: 'auth/setCompany',
			payload: companyResponse,
		})
		rootStore.commit({
			type: 'auth/setIsTeamMember',
			payload: Boolean(userResponse?.role_name),
		})
		rootStore.commit({
			type: 'auth/setRole',
			payload: userResponse?.role_name,
		})
	},
}

router.beforeEach(async (to, from, next) => {
	document.title = import.meta.env.VITE_TITLE
	const navigationGuardList = Object.values(navigationGuards)

	for (const navigationGuard of navigationGuardList) {
		const routeGuardResponse = await navigationGuard(to)
		if (routeGuardResponse) return next(routeGuardResponse)
	}

	next()
})

export default router
